// Tabbed modal for the three entry flows: Host new session, Join as player,
// Connect as IM. Replaces the body-of-the-page panels on Landing.

import { useRef, useState } from "react";
import { useDialog } from "./useDialog";

type Tab = "host" | "join" | "im";

interface Props {
  initialTab?: Tab;
  busy?: boolean;
  onClose: () => void;
  onCreate: () => void;
  onJoin: (sessionToken: string) => void;
  onIm: (sessionToken: string, imToken: string) => void;
}

export function EntryModal({ initialTab = "host", busy, onClose, onCreate, onJoin, onIm }: Props) {
  const [tab, setTab] = useState<Tab>(initialTab);
  const [joinSession, setJoinSession] = useState("");
  const [imSession, setImSession] = useState("");
  const [imToken, setImToken] = useState("");
  const dialogRef = useRef<HTMLDivElement>(null);
  useDialog(dialogRef, onClose);

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className="modal"
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label="Start or join a session"
        style={{ maxWidth: 520 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-body">
          <div style={{ display: "flex", gap: 6, marginBottom: 18, flexWrap: "wrap" }}>
            <button className={tab === "host" ? "primary" : ""} onClick={() => setTab("host")}>
              Host new
            </button>
            <button className={tab === "join" ? "primary" : ""} onClick={() => setTab("join")}>
              Join as player
            </button>
            <button className={tab === "im" ? "primary" : ""} onClick={() => setTab("im")}>
              Connect as IM
            </button>
            <div style={{ flex: 1 }} />
            <button onClick={onClose} aria-label="Close">×</button>
          </div>

          {tab === "host" && (
            <>
              <h2 style={{ margin: "0 0 10px", fontSize: 18 }}>Host a new session</h2>
              <p style={{ fontSize: 13, lineHeight: 1.6, color: "var(--color-text-muted)" }}>
                You'll get a SESSION token to share with teams and an IM token (privileged — keep
                private). Once you have them, the scenario catalogue opens for you to pick or swap.
              </p>
              <button
                className="primary"
                disabled={busy}
                onClick={onCreate}
                style={{ marginTop: 14, width: "100%" }}
              >
                ▶ Create session
              </button>
            </>
          )}

          {tab === "join" && (
            <>
              <h2 style={{ margin: "0 0 10px", fontSize: 18 }}>Join a session as a player</h2>
              <p style={{ fontSize: 13, lineHeight: 1.6, color: "var(--color-text-muted)" }}>
                Paste the SESSION token your IM shared. You'll see a brief on the next screen
                before committing to a team.
              </p>
              <input
                type="text"
                value={joinSession}
                onChange={(e) => setJoinSession(e.target.value)}
                placeholder="SESSION-XXXX-XXXX-..."
                aria-label="Session token"
                style={{ marginTop: 12 }}
              />
              <button
                onClick={() => onJoin(joinSession.trim())}
                disabled={!joinSession.trim()}
                className="primary"
                style={{ marginTop: 12, width: "100%" }}
              >
                Continue →
              </button>
            </>
          )}

          {tab === "im" && (
            <>
              <h2 style={{ margin: "0 0 10px", fontSize: 18 }}>Connect as Incident Master</h2>
              <p style={{ fontSize: 13, lineHeight: 1.6, color: "var(--color-text-muted)" }}>
                Paste the SESSION + IM tokens from when this session was created.
              </p>
              <input
                type="text"
                value={imSession}
                onChange={(e) => setImSession(e.target.value)}
                placeholder="SESSION-XXXX-XXXX-..."
                aria-label="Session token"
                style={{ marginTop: 12 }}
              />
              <div style={{ height: 8 }} />
              <input
                type="text"
                value={imToken}
                onChange={(e) => setImToken(e.target.value)}
                placeholder="IM-XXXX-XXXX-..."
                aria-label="IM token"
              />
              <button
                onClick={() => onIm(imSession.trim(), imToken.trim())}
                disabled={!imSession.trim() || !imToken.trim()}
                className="primary"
                style={{ marginTop: 12, width: "100%" }}
              >
                Connect
              </button>
              <div className="token-warn" style={{ marginTop: 10 }}>
                ⚠ The IM token is privileged — never paste it in shared chat.
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
